/**
 * Professional PDF Generation for SuiOS
 * Generates branded PDFs for Invoice, Purchase Order, and Production Sheet
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Brand colors
const COLORS = {
  navy: [27, 42, 74],      // #1B2A4A - Primary
  gold: [197, 165, 90],    // #C5A55A - Accent
  gray: [107, 114, 128],   // #6B7280 - Secondary text
  lightGray: [243, 244, 246], // #F3F4F6 - Background
  green: [5, 150, 105],    // #059669 - Success
  red: [220, 38, 38],      // #DC2626 - Error
  white: [255, 255, 255],
};

const CURRENCY_SYMBOLS = { INR: '₹', AED: 'AED ', USD: '$', GBP: '£' };

/**
 * Format currency value
 */
function formatCurrency(amount, currency = 'INR') {
  if (amount === null || amount === undefined) return '-';
  const symbol = CURRENCY_SYMBOLS[currency] || '₹';
  return `${symbol}${Number(amount).toLocaleString('en-IN')}`;
}

/**
 * Format date
 */
function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
}

/**
 * Add company header to PDF
 */
function addHeader(doc, title, subtitle = '') {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Gold accent line at top
  doc.setFillColor(...COLORS.gold);
  doc.rect(0, 0, pageWidth, 3, 'F');
  
  // Company name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.navy);
  doc.text('Herringbone & Sui', 20, 20);
  
  // Tagline
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Bespoke Menswear Tailoring', 20, 27);
  
  // Document title on right
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.navy);
  doc.text(title, pageWidth - 20, 18, { align: 'right' });
  
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.gray);
    doc.text(subtitle, pageWidth - 20, 26, { align: 'right' });
  }
  
  // Separator line
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.5);
  doc.line(20, 35, pageWidth - 20, 35);
  
  return 42; // Return Y position after header
}

/**
 * Add footer to PDF
 */
function addFooter(doc, pageNum, totalPages) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Footer line
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.3);
  doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);
  
  // Footer text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text('Herringbone & Sui | Mumbai', 20, pageHeight - 12);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 20, pageHeight - 12, { align: 'right' });
  
  // Generated date
  const now = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  doc.text(`Generated: ${now}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
}

/**
 * Add section title
 */
function addSectionTitle(doc, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.navy);
  doc.text(title, 20, y);
  return y + 6;
}

/**
 * Add key-value row
 */
function addKeyValue(doc, y, label, value, x = 20) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text(label + ':', x, y);
  doc.setTextColor(...COLORS.navy);
  doc.text(String(value || '-'), x + 45, y);
  return y + 5;
}

/**
 * Generate Client Invoice PDF
 */
export function generateInvoicePDF(order) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const currency = order.currency || 'INR';
  
  // Header
  let y = addHeader(doc, 'INVOICE', `#${order.order_number || order.id?.slice(-8).toUpperCase()}`);
  
  // Invoice Details Box (left) and Client Details Box (right)
  const boxWidth = (pageWidth - 50) / 2;
  
  // Invoice Details
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20, y, boxWidth, 35, 2, 2, 'F');
  y += 8;
  y = addKeyValue(doc, y, 'Invoice Date', formatDate(order.created_at || new Date()), 25);
  y = addKeyValue(doc, y, 'Due Date', formatDate(order.due_date), 25);
  y = addKeyValue(doc, y, 'Service Mode', order.service_mode === 'remote' ? 'Remote' : 'In-Store', 25);
  if (order.is_express) {
    doc.setTextColor(...COLORS.gold);
    y = addKeyValue(doc, y, 'Priority', 'EXPRESS ORDER', 25);
  }
  
  // Client Details
  const clientX = 25 + boxWidth + 10;
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20 + boxWidth + 5, y - 31, boxWidth, 35, 2, 2, 'F');
  
  let clientY = y - 23;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.navy);
  doc.text('BILL TO', clientX, clientY);
  clientY += 7;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(order.client_name || 'Client', clientX, clientY);
  clientY += 5;
  if (order.client_phone) {
    doc.setTextColor(...COLORS.gray);
    doc.text(order.client_phone, clientX, clientY);
  }
  
  y += 15;
  
  // Order Items Table
  y = addSectionTitle(doc, y, 'ORDER ITEMS');
  
  const itemsData = (order.items || []).map((item, idx) => [
    idx + 1,
    item.garment_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Item',
    item.fabric_code || '-',
    item.quantity || 1,
    formatCurrency(item.price, currency),
    formatCurrency((item.price || 0) * (item.quantity || 1), currency),
  ]);
  
  doc.autoTable({
    startY: y,
    head: [['#', 'Garment', 'Fabric', 'Qty', 'Unit Price', 'Amount']],
    body: itemsData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.navy,
      textColor: COLORS.white,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 9, textColor: COLORS.navy },
    alternateRowStyles: { fillColor: COLORS.lightGray },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 45 },
      2: { cellWidth: 35 },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 35, halign: 'right' },
    },
    margin: { left: 20, right: 20 },
  });
  
  y = doc.lastAutoTable.finalY + 10;
  
  // Totals Section
  const totalsX = pageWidth - 90;
  
  // Subtotal
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Subtotal:', totalsX, y);
  doc.setTextColor(...COLORS.navy);
  doc.text(formatCurrency(order.subtotal || order.total_amount, currency), pageWidth - 20, y, { align: 'right' });
  y += 6;
  
  // Express Surcharge if applicable
  if (order.express_surcharge && order.express_surcharge > 0) {
    doc.setTextColor(...COLORS.gold);
    doc.text('Express Surcharge:', totalsX, y);
    doc.text(formatCurrency(order.express_surcharge, currency), pageWidth - 20, y, { align: 'right' });
    y += 6;
  }
  
  // Adjustments
  if (order.adjustments && order.adjustments !== 0) {
    const adjColor = order.adjustments > 0 ? COLORS.red : COLORS.green;
    doc.setTextColor(...adjColor);
    doc.text('Adjustments:', totalsX, y);
    doc.text(formatCurrency(order.adjustments, currency), pageWidth - 20, y, { align: 'right' });
    y += 6;
  }
  
  // Total line
  doc.setDrawColor(...COLORS.navy);
  doc.setLineWidth(0.5);
  doc.line(totalsX - 5, y, pageWidth - 15, y);
  y += 6;
  
  // Grand Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.navy);
  doc.text('TOTAL:', totalsX, y);
  doc.text(formatCurrency(order.total_amount, currency), pageWidth - 20, y, { align: 'right' });
  y += 8;
  
  // Payment Status
  const paid = order.paid_amount || 0;
  const balance = (order.total_amount || 0) - paid;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.green);
  doc.text('Paid:', totalsX, y);
  doc.text(formatCurrency(paid, currency), pageWidth - 20, y, { align: 'right' });
  y += 6;
  
  if (balance > 0) {
    doc.setTextColor(...COLORS.red);
    doc.text('Balance Due:', totalsX, y);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(balance, currency), pageWidth - 20, y, { align: 'right' });
  } else {
    doc.setTextColor(...COLORS.green);
    doc.setFont('helvetica', 'bold');
    doc.text('PAID IN FULL', pageWidth - 20, y, { align: 'right' });
  }
  
  y += 15;
  
  // Notes section if present
  if (order.notes) {
    y = addSectionTitle(doc, y, 'NOTES');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    const splitNotes = doc.splitTextToSize(order.notes, pageWidth - 40);
    doc.text(splitNotes, 20, y);
  }
  
  // Footer
  addFooter(doc, 1, 1);
  
  return doc;
}

/**
 * Generate Purchase Order PDF
 */
export function generatePurchaseOrderPDF(po) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  let y = addHeader(doc, 'PURCHASE ORDER', `#${po.po_number || po.id?.slice(-8).toUpperCase()}`);
  
  // PO Details and Vendor Details boxes
  const boxWidth = (pageWidth - 50) / 2;
  
  // PO Details Box
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20, y, boxWidth, 40, 2, 2, 'F');
  let detailY = y + 8;
  detailY = addKeyValue(doc, detailY, 'PO Date', formatDate(po.created_at), 25);
  detailY = addKeyValue(doc, detailY, 'Status', (po.status || 'draft').replace(/_/g, ' ').toUpperCase(), 25);
  detailY = addKeyValue(doc, detailY, 'Required By', formatDate(po.required_by_date), 25);
  if (po.order_number) {
    detailY = addKeyValue(doc, detailY, 'Order Ref', po.order_number, 25);
  }
  
  // Vendor Details Box
  const vendorX = 25 + boxWidth + 10;
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20 + boxWidth + 5, y, boxWidth, 40, 2, 2, 'F');
  
  let vendorY = y + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.navy);
  doc.text('VENDOR', vendorX, vendorY);
  vendorY += 7;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(po.vendor_name || 'Vendor', vendorX, vendorY);
  vendorY += 5;
  
  if (po.vendor_contact) {
    doc.setTextColor(...COLORS.gray);
    doc.text(po.vendor_contact, vendorX, vendorY);
    vendorY += 5;
  }
  if (po.vendor_phone) {
    doc.text(po.vendor_phone, vendorX, vendorY);
  }
  
  y += 50;
  
  // Line Items Table
  y = addSectionTitle(doc, y, 'LINE ITEMS');
  
  const hasGst = (po.gst_rate_pct || 0) > 0;
  const headRow = hasGst
    ? [['#', 'Fabric Code', 'Qty (m)', 'Rate/m', 'Amount', `GST ${po.gst_rate_pct}%`, 'Total']]
    : [['#', 'Fabric Code', 'Mill', 'Quantity', 'Rate/m', 'Amount']];

  const itemsData = (po.items || []).map((item, idx) => {
    if (hasGst) {
      return [
        idx + 1,
        item.fabric_code || item.description || 'Item',
        `${item.quantity || 0} m`,
        formatCurrency(item.rate, 'INR'),
        formatCurrency((item.quantity || 0) * (item.rate || 0), 'INR'),
        formatCurrency(item.gst_amount || ((item.quantity || 0) * (item.rate || 0) * (po.gst_rate_pct || 0) / 100), 'INR'),
        formatCurrency(item.total_with_gst || ((item.quantity || 0) * (item.rate || 0) * (1 + (po.gst_rate_pct || 0) / 100)), 'INR'),
      ];
    }
    return [
      idx + 1,
      item.fabric_code || item.description || 'Item',
      item.mill_name || '-',
      `${item.quantity || 0} m`,
      formatCurrency(item.rate, 'INR'),
      formatCurrency((item.quantity || 0) * (item.rate || 0), 'INR'),
    ];
  });
  
  const colStyles = hasGst
    ? { 0: { cellWidth: 8, halign: 'center' }, 1: { cellWidth: 40 }, 2: { cellWidth: 20, halign: 'right' }, 3: { cellWidth: 22, halign: 'right' }, 4: { cellWidth: 25, halign: 'right' }, 5: { cellWidth: 22, halign: 'right' }, 6: { cellWidth: 28, halign: 'right' } }
    : { 0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 45 }, 2: { cellWidth: 35 }, 3: { cellWidth: 25, halign: 'right' }, 4: { cellWidth: 25, halign: 'right' }, 5: { cellWidth: 30, halign: 'right' } };

  doc.autoTable({
    startY: y,
    head: headRow,
    body: itemsData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.navy,
      textColor: COLORS.white,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 9, textColor: COLORS.navy },
    alternateRowStyles: { fillColor: COLORS.lightGray },
    columnStyles: colStyles,
    margin: { left: 20, right: 20 },
  });
  
  y = doc.lastAutoTable.finalY + 10;
  
  // Totals
  const totalsX = pageWidth - 80;
  
  if (hasGst) {
    // Subtotal
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    doc.text('Subtotal:', totalsX, y);
    doc.setTextColor(...COLORS.navy);
    doc.text(formatCurrency(po.subtotal || po.total_amount, 'INR'), pageWidth - 20, y, { align: 'right' });
    y += 6;

    // GST
    doc.setTextColor(...COLORS.gray);
    doc.text(`GST (${po.gst_rate_pct}%):`, totalsX, y);
    doc.setTextColor(...COLORS.navy);
    doc.text(formatCurrency(po.gst_amount || 0, 'INR'), pageWidth - 20, y, { align: 'right' });
    y += 6;

    // Line
    doc.setDrawColor(...COLORS.navy);
    doc.setLineWidth(0.5);
    doc.line(totalsX - 5, y, pageWidth - 15, y);
    y += 6;
  }
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.navy);
  doc.text('TOTAL:', totalsX, y);
  doc.text(formatCurrency(po.total_amount, 'INR'), pageWidth - 20, y, { align: 'right' });
  
  y += 15;
  
  // Terms & Notes
  if (po.notes || po.terms) {
    y = addSectionTitle(doc, y, 'NOTES & TERMS');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.gray);
    if (po.notes) {
      const splitNotes = doc.splitTextToSize(po.notes, pageWidth - 40);
      doc.text(splitNotes, 20, y);
      y += splitNotes.length * 5 + 5;
    }
    if (po.terms) {
      const splitTerms = doc.splitTextToSize(po.terms, pageWidth - 40);
      doc.text(splitTerms, 20, y);
    }
  }
  
  // Signature area
  y = doc.internal.pageSize.getHeight() - 50;
  doc.setDrawColor(...COLORS.gray);
  doc.setLineWidth(0.3);
  doc.line(20, y, 80, y);
  doc.line(pageWidth - 80, y, pageWidth - 20, y);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text('Authorized Signature', 20, y + 5);
  doc.text('Vendor Acceptance', pageWidth - 80, y + 5);
  
  // Footer
  addFooter(doc, 1, 1);
  
  return doc;
}

/**
 * Generate Production Sheet PDF
 */
export function generateProductionSheetPDF(tasks, date = new Date()) {
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.getWidth();
  const dateStr = date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  
  // Header
  let y = addHeader(doc, 'DAILY PRODUCTION SHEET', dateStr);
  
  // Summary stats
  const totalTasks = tasks.length;
  const byStatus = tasks.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});
  
  // Summary boxes
  const summaryItems = [
    { label: 'Total Tasks', value: totalTasks, color: COLORS.navy },
    { label: 'Queued', value: byStatus.queued || 0, color: COLORS.gray },
    { label: 'In Progress', value: byStatus.in_progress || 0, color: COLORS.gold },
    { label: 'Completed Today', value: byStatus.completed || 0, color: COLORS.green },
    { label: 'Outsourced', value: tasks.filter(t => t.is_outsourced).length, color: [128, 128, 128] },
  ];
  
  const boxW = (pageWidth - 60) / summaryItems.length;
  summaryItems.forEach((item, idx) => {
    const x = 20 + idx * (boxW + 5);
    doc.setFillColor(...item.color);
    doc.roundedRect(x, y, boxW, 18, 2, 2, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.white);
    doc.text(String(item.value), x + boxW / 2, y + 8, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(item.label, x + boxW / 2, y + 14, { align: 'center' });
  });
  
  y += 28;
  
  // Tasks Table
  y = addSectionTitle(doc, y, 'TASKS');
  
  const tableData = tasks.slice(0, 30).map((task, idx) => [
    idx + 1,
    task.order_number || '-',
    task.client_name || '-',
    task.garment_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-',
    task.task_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-',
    task.worker_name || 'Unassigned',
    (task.status || 'queued').replace(/_/g, ' ').toUpperCase(),
    task.is_outsourced ? 'YES' : '',
    '', // Checkbox column for completion
  ]);
  
  doc.autoTable({
    startY: y,
    head: [['#', 'Order', 'Client', 'Garment', 'Task', 'Worker', 'Status', 'Out', '✓']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.navy,
      textColor: COLORS.white,
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: { fontSize: 8, textColor: COLORS.navy },
    alternateRowStyles: { fillColor: COLORS.lightGray },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 25 },
      2: { cellWidth: 35 },
      3: { cellWidth: 30 },
      4: { cellWidth: 30 },
      5: { cellWidth: 30 },
      6: { cellWidth: 25, halign: 'center' },
      7: { cellWidth: 12, halign: 'center' },
      8: { cellWidth: 12, halign: 'center' },
    },
    margin: { left: 20, right: 20 },
    didDrawCell: (data) => {
      // Draw checkbox in last column
      if (data.column.index === 8 && data.section === 'body') {
        const dim = 4;
        doc.setDrawColor(...COLORS.navy);
        doc.setLineWidth(0.3);
        doc.rect(data.cell.x + (data.cell.width - dim) / 2, data.cell.y + (data.cell.height - dim) / 2, dim, dim);
      }
    },
  });
  
  // Worker assignment section at bottom
  y = doc.lastAutoTable.finalY + 15;
  
  // Group tasks by worker
  const byWorker = tasks.reduce((acc, t) => {
    const worker = t.worker_name || 'Unassigned';
    if (!acc[worker]) acc[worker] = [];
    acc[worker].push(t);
    return acc;
  }, {});
  
  if (Object.keys(byWorker).length > 0 && y < doc.internal.pageSize.getHeight() - 40) {
    y = addSectionTitle(doc, y, 'BY WORKER');
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    let col = 0;
    const colWidth = (pageWidth - 40) / 3;
    
    Object.entries(byWorker).slice(0, 9).forEach(([worker, workerTasks], idx) => {
      if (idx > 0 && idx % 3 === 0) {
        y += 20;
        col = 0;
      }
      const x = 20 + col * colWidth;
      
      doc.setTextColor(...COLORS.navy);
      doc.setFont('helvetica', 'bold');
      doc.text(`${worker}: ${workerTasks.length} tasks`, x, y);
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.gray);
      const taskList = workerTasks.slice(0, 3).map(t => `• ${t.garment_type || 'Item'}`).join(', ');
      doc.text(doc.splitTextToSize(taskList, colWidth - 10), x, y + 5);
      
      col++;
    });
  }
  
  // Footer
  addFooter(doc, 1, 1);
  
  return doc;
}

/**
 * Download PDF with given filename
 */
export function downloadPDF(doc, filename) {
  doc.save(filename);
}

/**
 * Generate Measurement PDF
 * Groups by body area, shows only fields with values, dual units
 */
export function generateMeasurementPDF({ clientName, measuredDate, measuredBy, garmentContext, values }) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  let y = addHeader(doc, 'MEASUREMENTS', clientName || 'Client');

  // Info box
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(20, y, pageWidth - 40, 22, 2, 2, 'F');
  let infoY = y + 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.gray);
  doc.text('Client:', 25, infoY);
  doc.setTextColor(...COLORS.navy);
  doc.text(clientName || '-', 50, infoY);

  doc.setTextColor(...COLORS.gray);
  doc.text('Measured:', 100, infoY);
  doc.setTextColor(...COLORS.navy);
  doc.text(formatDate(measuredDate), 128, infoY);

  infoY += 6;
  doc.setTextColor(...COLORS.gray);
  doc.text('By:', 25, infoY);
  doc.setTextColor(...COLORS.navy);
  doc.text(measuredBy || '-', 50, infoY);

  doc.setTextColor(...COLORS.gray);
  doc.text('Context:', 100, infoY);
  doc.setTextColor(...COLORS.navy);
  doc.text((garmentContext || 'general').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), 128, infoY);

  y += 30;

  // Group fields by body area
  const UPPER_BODY = ['neck', 'shoulder_width', 'shoulder_drop', 'cross_front', 'cross_back', 'chest', 'lower_chest', 'stomach', 'waist', 'hips', 'jacket_length', 'shirt_length', 'back_length', 'waistcoat_length', 'kurta_length'];
  const ARMS = ['bicep', 'forearm', 'wrist', 'sleeve_length'];
  const LOWER_BODY = ['trouser_waist', 'u_rise', 'thigh', 'thigh_lower', 'knee', 'calf', 'ankle', 'inseam', 'trouser_length', 'trouser_bottom_opening'];
  const OTHER = ['height', 'weight'];

  const filledValues = (values || []).filter(v => v.value_inches > 0 || v.value_cm > 0);

  const groups = [
    { title: 'UPPER BODY', fields: UPPER_BODY },
    { title: 'ARMS', fields: ARMS },
    { title: 'LOWER BODY', fields: LOWER_BODY },
    { title: 'OTHER', fields: OTHER },
  ];

  for (const group of groups) {
    const groupValues = filledValues.filter(v => group.fields.includes(v.field_name));
    if (groupValues.length === 0) continue;

    y = addSectionTitle(doc, y, group.title);

    const tableData = groupValues.map(v => [
      v.label || v.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      `${v.value_inches}"`,
      `${v.value_cm} cm`,
    ]);

    doc.autoTable({
      startY: y,
      head: [['Measurement', 'Inches', 'Centimeters']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.navy,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 9, textColor: COLORS.navy },
      alternateRowStyles: { fillColor: COLORS.lightGray },
      columnStyles: {
        0: { cellWidth: 65 },
        1: { cellWidth: 35, halign: 'center' },
        2: { cellWidth: 35, halign: 'center' },
      },
      margin: { left: 20, right: 20 },
      tableWidth: pageWidth - 40,
    });

    y = doc.lastAutoTable.finalY + 8;

    // Check page overflow
    if (y > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      y = 20;
    }
  }

  // Footer
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.3);
  doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text('Herringbone & Sui, Colaba, Mumbai', 20, pageHeight - 12);

  const now = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  doc.text(`Generated: ${now}`, pageWidth - 20, pageHeight - 12, { align: 'right' });

  return doc;
}

/**
 * Open PDF in new tab for preview
 */
export function previewPDF(doc) {
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}
